/**
 * Authentication Context
 * Includes tenant school selection for A2A cross-server communication.
 */

import { createContext, useState, useCallback } from 'react';
import { authService } from '../api/services/authService';

export const AuthContext = createContext(null);

const TENANT_STORAGE_KEY = 'selectedTenant';

function loadStoredTenant() {
  try {
    const raw = localStorage.getItem(TENANT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  const [selectedTenant, setSelectedTenantState] = useState(loadStoredTenant);

  const setSelectedTenant = useCallback((tenant) => {
    setSelectedTenantState(tenant);
    if (tenant) {
      localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(tenant));
    } else {
      localStorage.removeItem(TENANT_STORAGE_KEY);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login({ email, password });
      
      if (response?.status === 'success' && response?.data?.user) {
        setUser(response.data.user);
        return { success: true, user: response.data.user };
      }
      
      return { success: false, error: response?.message || 'Login failed' };
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    setSelectedTenantState(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(TENANT_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, setUser, isAuthenticated: !!user,
      selectedTenant, setSelectedTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
